import { useRef, useState } from 'react'
import {
  accepterManuellement, deposerScan, envoyerContrat, urlSignee, type DossierPme, type SignatureDossier,
} from '../../api/onboarding'
import {
  COULEUR_SIGNATURE, LIBELLE_METHODE, LIBELLE_STATUT_SIGNATURE, messageSignature, optionsEnvoiContrat, signatureCourante, signatureEnCours,
  type MoyenEnvoiContrat, type StatutOnboarding,
} from '../../api/onboardingRegles'
import { nomSignataire } from '../../api/designationPartie'
import { afficherTelephone } from '../../api/commun'
import { lienWhatsApp, normaliserNumero } from '../../api/whatsapp'
import { Bloc, Erreur, Info, useAction } from './Briques'
import { dateComplete } from './dates'

/**
 * Section « Contrat » : la signature courante et les deux chemins.
 *   (a) SIGNATURE ÉLECTRONIQUE (Documenso) — l'edge function `envoyer-contrat-signature` rend le contrat
 *       publié (variables du dossier), crée le document et renvoie un LIEN, partagé par e-mail (Documenso
 *       l'envoie) ou par WhatsApp (wa.me vers le téléphone du client, message relu et envoyé par l'humain) :
 *       deux options ÉGALES, choisies avant l'envoi (`optionsEnvoiContrat` dit laquelle est possible). Un client
 *       sans e-mail mais avec un numéro se signe par WhatsApp — l'edge function le gère (adresse technique côté
 *       Documenso, e-mail jamais envoyé). Le bouton est TOUJOURS là : si Documenso n'est pas configuré, la phrase l'explique.
 *   (b) ACCEPTATION MANUELLE — propriétaire seulement, note obligatoire (ex. « contrat papier signé le …,
 *       scan déposé »), consignée dans le journal. Pour le contrat signé sur papier.
 */
export function SectionContrat({
  orgId, dossier, statut, signatures, contrat, peutGerer, proprietaire, pret, surChange,
}: {
  orgId: string
  dossier: DossierPme
  statut: StatutOnboarding
  signatures: SignatureDossier[]
  contrat: { id: string; version: number } | null
  peutGerer: boolean
  proprietaire: boolean
  /** Raison pour laquelle l'envoi est impossible (dite en une phrase), ou null si le dossier est prêt. */
  pret: string | null
  surChange: () => void
}) {
  const courante = signatureCourante(signatures)
  const enCours = signatureEnCours(signatures)
  const options = optionsEnvoiContrat(dossier)
  const [choix, setChoix] = useState<MoyenEnvoiContrat | null>(null)
  // Le choix de la personne tant qu'il reste possible ; sinon l'option par défaut (e-mail s'il existe, sinon WhatsApp).
  const moyen: MoyenEnvoiContrat | null = choix && options[choix] ? choix : options.defaut
  const [lien, setLien] = useState<string | null>(null)
  const [wa, setWa] = useState<string | null>(null)
  const [copie, setCopie] = useState(false)
  const [note, setNote] = useState('')
  const [manuel, setManuel] = useState(false)
  const scan = useRef<HTMLInputElement>(null)
  const envoi = useAction()
  const accept = useAction()
  const pdf = useAction()

  const lienAffiche = lien ?? enCours?.lien_signature ?? null
  const waAffiche = wa ?? (lienAffiche ? waDepuisFiche(dossier, lienAffiche) : null)
  const dejaSigne = courante?.status === 'signe'
  const etapeOk = statut === 'documents' || statut === 'contrat_envoye'
  const raison = !peutGerer ? 'Votre rôle ne permet pas d’envoyer un contrat.' : dejaSigne ? 'Le contrat est déjà signé.' : !etapeOk ? 'Le dossier n’est pas à l’étape d’envoi du contrat.' : pret ?? options.blocage

  function envoyer() {
    // Lien déjà créé et envoi par WhatsApp : rien à demander au serveur, on ouvre WhatsApp avec ce lien.
    if (enCours && moyen === 'whatsapp' && waAffiche) {
      window.open(waAffiche, '_blank', 'noopener,noreferrer')
      return
    }
    void envoi.lancer(async () => {
      const r = await envoyerContrat(dossier.id, moyen === 'email')
      setLien(r.lien_signature)
      setWa(r.whatsapp_url)
      surChange()
      if (r.email_impossible) {
        return 'Ce contrat a été préparé sans adresse e-mail : il ne peut pas partir par e-mail. Envoyez-le par WhatsApp.'
      }
      if (r.email_envoye) {
        return r.deja_envoye ? 'Lien de signature renvoyé par e-mail par Documenso.' : 'Lien créé et e-mail envoyé par Documenso.'
      }
      return moyen === 'whatsapp'
        ? 'Lien créé. Cliquez sur « Envoyer par WhatsApp » pour le faire parvenir : aucun e-mail n’est parti.'
        : 'Lien créé. Copiez-le ou envoyez-le par WhatsApp : aucun e-mail n’est parti.'
    })
  }

  async function copier(texte: string) {
    try {
      await navigator.clipboard.writeText(texte)
      setCopie(true)
      window.setTimeout(() => setCopie(false), 2000)
    } catch {
      window.prompt('Copiez ce lien :', texte)
    }
  }

  function accepter() {
    if (note.trim().length < 10) { accept.signaler('Écrivez la note (10 caractères au moins) : par exemple « contrat papier signé le 5 octobre, scan déposé ».'); return }
    if (!window.confirm('Enregistrer l’acceptation MANUELLE du contrat ?\n\nCette décision est définitive et sera inscrite au journal avec votre note.')) return
    void accept.lancer(async () => {
      const f = scan.current?.files?.[0]
      const chemin = f ? await deposerScan(orgId, dossier.id, f) : null
      await accepterManuellement(dossier.id, note, chemin)
      setNote('')
      setManuel(false)
      surChange()
      return 'Acceptation manuelle enregistrée : le contrat est signé.'
    })
  }

  function ouvrirPdf(chemin: string) {
    void pdf.lancer(async () => { window.open(await urlSignee(chemin), '_blank', 'noopener,noreferrer') })
  }

  return (
    <Bloc titre="Contrat" droite={contrat ? `Modèle publié : v${contrat.version}` : 'Aucun modèle publié'} lecture={!peutGerer}>
      {courante ? (
        <dl className="onb-kv">
          <dt>Méthode</dt><dd>{LIBELLE_METHODE[courante.method]}</dd>
          <dt>Statut</dt><dd><span className={`badge ${COULEUR_SIGNATURE[courante.status]}`}>{LIBELLE_STATUT_SIGNATURE[courante.status]}</span></dd>
          <dt>Version</dt><dd>v{courante.template_version}{contrat && courante.template_version < contrat.version ? ' (dépassée : une version plus récente est publiée)' : ''}</dd>
          <dt>{courante.status === 'signe' ? 'Signé le' : 'Envoyé le'}</dt><dd>{dateComplete(courante.accepted_at ?? courante.created_at)}</dd>
          {courante.destinataire_email && <><dt>Envoyé à</dt><dd>{courante.destinataire_email}</dd></>}
          {courante.note_manuelle && <><dt>Note</dt><dd>{courante.note_manuelle}</dd></>}
          {courante.status === 'signe' && courante.pdf_path && (
            <><dt>Document signé</dt><dd><button className="btn btn-outline btn-sm" disabled={pdf.occupe} onClick={() => ouvrirPdf(courante.pdf_path as string)}>Ouvrir le PDF</button></dd></>
          )}
        </dl>
      ) : (
        <p className="onb-note">Aucune signature pour l’instant.</p>
      )}
      <Erreur texte={pdf.erreur} />

      {peutGerer && !dejaSigne && (
        <>
          <div className="onb-sep" />
          <p className="onb-note"><b>Signature électronique (Documenso)</b> : crée le document et renvoie un lien de signature. Choisissez comment le faire parvenir au signataire.</p>
          <fieldset className="onb-moyens" style={{ border: 0, margin: '6px 0', padding: 0 }}>
            <legend className="onb-note" style={{ padding: 0 }}>Envoyer le lien</legend>
            <label className="onb-coche" style={{ margin: '4px 0' }} title={options.raisonEmail ?? undefined}>
              <input type="radio" name="moyen-envoi-contrat" checked={moyen === 'email'} disabled={!options.email} onChange={() => setChoix('email')} />
              <span>Par e-mail : Documenso envoie le lien{dossier.email ? ` à ${dossier.email}` : ''}</span>
            </label>
            {options.raisonEmail && <div className="onb-note" style={{ margin: '0 0 4px 24px' }}>{options.raisonEmail}</div>}
            <label className="onb-coche" style={{ margin: '4px 0' }} title={options.raisonWhatsapp ?? undefined}>
              <input type="radio" name="moyen-envoi-contrat" checked={moyen === 'whatsapp'} disabled={!options.whatsapp} onChange={() => setChoix('whatsapp')} />
              <span>Par WhatsApp : vous envoyez le lien{dossier.phone ? ` au ${afficherTelephone(dossier.phone) || dossier.phone}` : ''}, aucun e-mail ne part</span>
            </label>
            {options.raisonWhatsapp && <div className="onb-note" style={{ margin: '0 0 4px 24px' }}>{options.raisonWhatsapp}</div>}
          </fieldset>
          <div className="onb-actions" style={{ marginTop: 4 }}>
            <button className="btn btn-gold btn-sm" disabled={envoi.occupe || raison !== null || moyen === null} onClick={envoyer}
              title={raison ?? undefined}>
              {envoi.occupe ? 'Envoi…' : enCours ? (moyen === 'whatsapp' ? 'Envoyer par WhatsApp' : 'Renvoyer par e-mail') : moyen === 'whatsapp' ? 'Préparer le lien et l’envoyer par WhatsApp' : 'Envoyer le contrat par e-mail'}
            </button>
          </div>
          {raison && <div className="onb-alerte">{raison}</div>}
          <Erreur texte={envoi.erreur} />
          <Info texte={envoi.info} />
        </>
      )}

      {lienAffiche && !dejaSigne && (
        <div style={{ marginTop: 10 }}>
          <p className="onb-note">{lien ? 'Lien de signature :' : 'Un lien de signature a déjà été envoyé :'}</p>
          <div className="onb-lien">
            <input readOnly value={lienAffiche} aria-label="Lien de signature" onFocus={(e) => e.currentTarget.select()} />
            <button className="btn btn-outline btn-sm" onClick={() => void copier(lienAffiche)}>{copie ? 'Copié' : 'Copier'}</button>
          </div>
          <div className="onb-actions" style={{ marginTop: 4 }}>
            <button className="btn btn-green btn-sm" disabled={!waAffiche}
              title={waAffiche ? undefined : 'Numéro de téléphone absent ou illisible (il faut un numéro malien ou un indicatif : +223…)'}
              onClick={() => waAffiche && window.open(waAffiche, '_blank', 'noopener,noreferrer')}>
              Envoyer par WhatsApp
            </button>
            {!waAffiche && <span className="onb-note">Numéro WhatsApp invalide : copiez le lien.</span>}
          </div>
        </div>
      )}

      {proprietaire && !dejaSigne && etapeOk && (
        <>
          <div className="onb-sep" />
          {!manuel ? (
            <>
              <p className="onb-note"><b>Contrat signé sur papier ?</b> Le propriétaire peut enregistrer une acceptation manuelle (note obligatoire, journalisée).</p>
              <button className="btn btn-outline btn-sm" onClick={() => setManuel(true)}>Acceptation manuelle…</button>
            </>
          ) : (
            <>
              <p className="onb-note"><b>Acceptation manuelle</b> (propriétaire). Cette décision est définitive.</p>
              {pret && <div className="onb-alerte">{pret} L’enregistrement sera refusé tant que ce n’est pas fait : la base applique les mêmes conditions qu’à l’envoi.</div>}
              <textarea className="onb-motif" placeholder="Note obligatoire, par exemple : contrat papier signé le 5 octobre par Mme Sangaré, scan déposé" value={note} onChange={(e) => setNote(e.target.value)} />
              <div className="field" style={{ marginBottom: 6 }}>
                <label>Scan du contrat signé (facultatif)</label>
                <input ref={scan} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" />
              </div>
              <div className="onb-actions" style={{ marginTop: 0 }}>
                <button className="btn btn-gold btn-sm" disabled={accept.occupe} onClick={accepter}>{accept.occupe ? 'Enregistrement…' : 'Enregistrer l’acceptation manuelle'}</button>
                <button className="btn btn-ghost btn-sm" disabled={accept.occupe} onClick={() => setManuel(false)}>Annuler</button>
              </div>
            </>
          )}
          <Erreur texte={accept.erreur} />
          <Info texte={accept.info} />
        </>
      )}
    </Bloc>
  )
}

/** Lien wa.me vers le téléphone de la fiche (cas d'un lien déjà envoyé, rouvert plus tard). */
function waDepuisFiche(d: DossierPme, lien: string): string | null {
  const numero = normaliserNumero(d.phone)
  if (!numero) return null
  return lienWhatsApp(numero, `${messageSignature(nomSignataire(d))}\n${lien}`)
}
